import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import type { Template } from '@/api/v4/template.api';
import { Inline } from '@/ui/line/line';
import { downloadLinkAutomatically } from '@/helpers/download-link-automatically';
import { Small } from '@/ui/typography/across-platform';
import { Button } from '@/ui/button/button';
import { SmallText } from '@/ui/typography/action-panel';
import { formatDate } from '@/helpers/format-date';

interface Props {
  template: Template;
}

export const TemplateCard = ({ template }: Props) => {
  const { t } = useTranslation('default', { keyPrefix: 'templatesSidebar' });

  return (
    <Flexbox
      name="template-card-container"
      padding="20px 40px"
      direction="column"
      gap="8px"
    >
      <Flexbox name="template-name" justify="space-between" fullWidth>
        <Inline>
          <Icon icon="FileDoc" />
          <FileName>{t([`fileTypes.${template.fileType}.name`])}</FileName>
        </Inline>
        <Button
          variant="text"
          disabled={!template.link}
          onClick={() =>
            template.link
              ? downloadLinkAutomatically(template.link, template.name)
              : void 0
          }
        >
          <Icon icon="ArrowDownFromBracket" />
        </Button>
      </Flexbox>

      <Description>
        {t([`fileTypes.${template.fileType}.description`])}
      </Description>
      <SmallText>
        {formatDate(template.createdDate, { format: 'MMM yyyy' })}
      </SmallText>
    </Flexbox>
  );
};

const FileName = styled('p', forwardRef)`
  ${({ theme }) => theme.typography.actionsPanel.files.fileName}

  max-width: 380px;
`;

const Description = styled(Small)`
  text-align: justify;
`;
